<template>
  <div class="flex flex-col w-full h-full justify-start items-center overflow-y-auto">
    <div class="flex flex-col w-full justify-start items-start">
      <div v-if="!connectionStatus?.connected" class="flex flex-col gap-2 mb-2">
        <span class="font-medium text-center px-2 rounded">חיבור WhatsApp Business</span>
        <span class="text-sm text-pretty px-2 rounded">
          חברו את מספר ה-WhatsApp Business שלכם כדי שתזכורות אוטומטיות יישלחו מהמספר שלכם ישירות.
          הלקוחות יראו את שם העסק שלכם כשולח ההודעה, ותוכלו להמשיך להשתמש באפליקציית WhatsApp Business כרגיל.
        </span>
      </div>

      <!-- Connected state -->
      <div v-if="connectionStatus?.connected" class="w-full h-full flex flex-col mb-24">
        <div class="text-sm bg-green-100 p-4 rounded-md mt-2 flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <i class="pi pi-check-circle text-green-600"></i>
            <strong>WhatsApp Business מחובר</strong>
          </div>
          <div v-if="connectionStatus.displayName" class="text-gray-700">
            <strong>שם: </strong>{{ connectionStatus.displayName }}
          </div>
          <div v-if="connectionStatus.connectedAt" class="text-gray-500 text-xs">
            חובר ב-{{ formatDate(connectionStatus.connectedAt) }}
          </div>
        </div>

        <div class="w-full fixed right-0 bottom-0 p-4 z-40 bg-gray-100"
             :style="{ paddingBottom: `${paddingBottom + 16}px` }">
          <Button
            :loading="loadingCta"
            label="נתק WhatsApp Business"
            class="p-button-danger shadow-xl w-full !rounded-3xl"
            style="display: flex; flex-grow: 1"
            @click="disconnectWhatsApp"
          />
        </div>
      </div>

      <!-- Disconnected state -->
      <div v-else class="w-full">
        <div class="text-sm bg-green-50 p-4 rounded mt-2 flex flex-col">
          <strong class="mb-2">איך זה עובד:</strong>
          <ul class="space-y-1">
            <li>בתהליך החיבור תקבלו QR Code לסריקה עם אפליקציית WhatsApp Business</li>
            <li>לאחר הסריקה, התזכורות יישלחו מהמספר שלכם — האפליקציה ממשיכה לעבוד כרגיל</li>
            <li>לקוחות יראו את שם העסק שלכם כשולח ולא את מספר המערכת</li>
          </ul>
        </div>

        <div class="text-sm bg-amber-50 p-4 rounded mt-2 flex flex-col">
          <strong class="mb-2">שימו לב:</strong>
          <ul class="space-y-1">
            <li>לאחר החיבור, התבניות (templates) צריכות אישור מ-Meta - זה לוקח בדרך כלל מספר דקות עד שעות</li>
            <li>עסקים חדשים מתחילים עם מגבלה של 250 הודעות ביום עד לאימות העסק מול Meta</li>
            <li>
              Meta גובה עלות קטנה לכל הודעה אוטומטית — יש להוסיף אמצעי תשלום בחשבון Meta Business שלכם.
              <a href="https://business.facebook.com/billing" target="_blank" rel="noopener"
                 class="underline text-amber-700">לחצו כאן להוספת אמצעי תשלום</a>
            </li>
          </ul>
        </div>

        <!-- Step-by-step tutorial -->
        <div class="text-sm mt-2 pb-28">
          <button
            type="button"
            class="flex items-center gap-1 text-blue-600 font-medium"
            @click="showTutorial = !showTutorial"
          >
            <i :class="showTutorial ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-xs"></i>
            מדריך חיבור שלב אחר שלב
          </button>
          <div v-show="showTutorial" class="mt-2 bg-gray-50 border border-gray-200 rounded p-3 flex flex-col gap-2">
            <ol class="list-decimal list-inside space-y-2 text-gray-700">
              <li>
                <strong>עדכנו את אפליקציית WhatsApp Business</strong> לגרסה 2.24.17 ומעלה (App Store / Google Play).
              </li>
              <li>
                <strong>לחצו על "חבר WhatsApp Business"</strong> — ייפתח חלון כניסה של Meta.
              </li>
              <li>
                <strong>התחברו עם חשבון Facebook</strong> — אישי או עסקי. אם אין לכם, תוכלו ליצור במקום.
              </li>
              <li>
                <strong>הזינו את מספר הטלפון</strong> של WhatsApp Business שלכם ואשרו אותו.
              </li>
              <li>
                <strong>סרקו את ה-QR Code שיוצג</strong>:
                <ul class="list-disc list-inside mr-4 mt-1 space-y-1 text-gray-600">
                  <li>פתחו את <strong>אפליקציית WhatsApp Business</strong> בטלפון</li>
                  <li>לחצו על ⋮ (שלוש נקודות) ← <strong>הגדרות</strong></li>
                  <li>לחצו על <strong>חשבון ← מכשירים מקושרים</strong></li>
                  <li>לחצו על <strong>"קישור מכשיר"</strong> וסרקו את הקוד</li>
                </ul>
              </li>
              <li>
                <strong>השלימו את התהליך בחלון Meta</strong> — לחצו "סיום" או "Continue".
              </li>
              <li>
                <strong>הוסיפו אמצעי תשלום</strong> בחשבון Meta Business שלכם ב-
                <a href="https://business.facebook.com/billing" target="_blank" rel="noopener"
                   class="underline text-blue-600">business.facebook.com/billing</a>
                כדי שMeta תוכל לחייב את עלות ההודעות.
              </li>
            </ol>
          </div>
        </div>

        <div class="w-full fixed right-0 bottom-0 p-4 z-40 bg-gray-100"
             :style="{ paddingBottom: `${paddingBottom + 16}px` }">
          <Button
            :loading="loadingCta"
            label="חבר WhatsApp Business"
            class="w-full"
            style="display: flex; flex-grow: 1"
            icon="pi pi-whatsapp"
            @click="launchEmbeddedSignup"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useToast } from 'vue-toastification';
import Button from 'primevue/button';
import { Capacitor } from '@capacitor/core';
import { useAdminStore } from '@app/stores';
import { SettingsApi } from '@app/api/admin';
import { useSharedStore } from '@torim-app/shared-components';
import { useConfirm } from 'primevue/useconfirm';
import { trackEvent } from '@app/composables/posthog';

const toast = useToast();
// OAuth code from Meta's redirect (native flow) — set when Integrations reopens this drawer
const props = defineProps<{ oauthCode?: string }>();
const adminStore = useAdminStore();
const sharedStore = useSharedStore();
const confirm = useConfirm();

const paddingBottom = computed(() => sharedStore.bottomPadding);

const loadingCta = ref(false);
const showTutorial = ref(false);

// Pre-populate from store so there's no flash when the drawer opens.
// fetchConnectionStatus() will enrich with displayName / aiEnabled once it resolves.
const connectionStatus = ref<{
  connected: boolean;
  displayName?: string;
  phoneNumberId?: string;
  connectedAt?: string;
  aiEnabled?: boolean;
} | null>(
  adminStore.getBusinessSiteData?.whatsappIntegration?.accessToken
    ? { connected: true, aiEnabled: adminStore.getBusinessSiteData.whatsappIntegration.aiEnabled ?? false }
    : { connected: false }
);

// Temporary storage for data received from the Embedded Signup popup
let pendingWabaId: string | null = null;
let pendingPhoneNumberId: string | null = null;

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString('he-IL');
  } catch {
    return dateStr;
  }
};

const fetchConnectionStatus = async () => {
  try {
    connectionStatus.value = await SettingsApi.getWhatsappConnectionStatus();
  } catch (e) {
    console.error('Failed to fetch WhatsApp connection status:', e);
  }
};

/**
 * Listen for postMessage events from the Facebook Embedded Signup popup.
 * These fire during the signup flow and contain the WABA ID and phone number ID.
 */
const handleFbMessage = (event: MessageEvent) => {
  if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
  console.log('[WA-DEBUG] Received postMessage from:', event.origin);
  try {
    const data = JSON.parse(event.data);
    console.log('[WA-DEBUG] Parsed FB message:', JSON.stringify(data));
    if (data.type === 'WA_EMBEDDED_SIGNUP') {
      console.log('[WA-DEBUG] WA_EMBEDDED_SIGNUP event:', data.event, 'data:', JSON.stringify(data.data));
      if (data.event === 'FINISH') {
        pendingWabaId = data.data?.waba_id;
        pendingPhoneNumberId = data.data?.phone_number_id;
        console.log('[WA-DEBUG] FINISH - wabaId:', pendingWabaId, 'phoneNumberId:', pendingPhoneNumberId);
      } else if (data.event === 'FINISH_ONLY_WABA') {
        pendingWabaId = data.data?.waba_id;
        pendingPhoneNumberId = null;
        console.log('[WA-DEBUG] FINISH_ONLY_WABA - wabaId:', pendingWabaId);
      } else if (data.event === 'CANCEL') {
        console.log('[WA-DEBUG] User cancelled WhatsApp Embedded Signup');
      } else if (data.event === 'ERROR') {
        console.error('[WA-DEBUG] WhatsApp Embedded Signup ERROR:', JSON.stringify(data.data));
      }
    }
  } catch {
    // Non-JSON messages from the Facebook iframe; safe to ignore
  }
};

const loadFbSdk = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const waAppId = import.meta.env.VITE_META_WA_APP_ID;
    const win = window as any;

    console.log('[WA-DEBUG] loadFbSdk called');
    console.log('[WA-DEBUG] VITE_META_WA_APP_ID:', waAppId);
    console.log('[WA-DEBUG] Current origin:', window.location.origin);
    console.log('[WA-DEBUG] Current URL:', window.location.href);
    console.log('[WA-DEBUG] Protocol:', window.location.protocol);

    if (!waAppId) {
      console.error('[WA-DEBUG] VITE_META_WA_APP_ID is missing!');
      reject(new Error('Missing VITE_META_WA_APP_ID'));
      return;
    }

    const initSdk = () => {
      console.log('[WA-DEBUG] Initializing FB SDK with appId:', waAppId, 'version: v25.0');
      try {
        win.FB.init({
          appId: waAppId,
          cookie: true,
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v25.0'
        });
        console.log('[WA-DEBUG] FB.init() completed successfully');
        resolve();
      } catch (e) {
        console.error('[WA-DEBUG] FB.init() threw error:', e);
        reject(e);
      }
    };

    if (win.FB) {
      console.log('[WA-DEBUG] FB SDK already loaded, re-initializing');
      initSdk();
      return;
    }

    console.log('[WA-DEBUG] FB SDK not loaded yet, loading script...');
    win.fbAsyncInit = function() {
      console.log('[WA-DEBUG] fbAsyncInit fired');
      initSdk();
    };

    if (!document.getElementById('facebook-jssdk')) {
      const js = document.createElement('script');
      js.id = 'facebook-jssdk';
      js.defer = true;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      js.onerror = (e) => {
        console.error('[WA-DEBUG] Failed to load FB SDK script:', e);
        reject(new Error('Failed to load Facebook SDK script'));
      };
      console.log('[WA-DEBUG] Appending FB SDK script to head');
      document.head.appendChild(js);
    } else {
      console.log('[WA-DEBUG] FB SDK script tag already exists but FB not initialized');
    }
  });
};

const launchEmbeddedSignup = async () => {
  console.log('[WA-DEBUG] === launchEmbeddedSignup START ===');
  console.log('[WA-DEBUG] Platform:', Capacitor.isNativePlatform() ? 'native' : 'web');
  console.log('[WA-DEBUG] VITE_META_WA_APP_ID:', import.meta.env.VITE_META_WA_APP_ID);
  console.log('[WA-DEBUG] VITE_META_EMBEDDED_SIGNUP_CONFIG_ID:', import.meta.env.VITE_META_EMBEDDED_SIGNUP_CONFIG_ID);
  console.log('[WA-DEBUG] VITE_DASHBOARD_URL:', import.meta.env.VITE_DASHBOARD_URL);

  trackEvent('whatsapp_connect_started', { platform: Capacitor.isNativePlatform() ? 'native' : 'web' });
  // On native Capacitor, the WebView runs on capacitor://localhost which is not a
  // registerable domain in Meta's app dashboard → "JSSDK unknown host domain" error.
  // Solution: open the Facebook OAuth URL directly in the system browser via window.open().
  // Facebook shows the WhatsApp Embedded Signup UI in Chrome/Safari, and on completion
  // redirects to the VITE_DASHBOARD_URL which is a registered Android App Link / Universal Link,
  // so the OS hands control back to the native app with the OAuth code in the URL.
  if (Capacitor.isNativePlatform()) {
    const appId = import.meta.env.VITE_META_WA_APP_ID;
    const configId = import.meta.env.VITE_META_EMBEDDED_SIGNUP_CONFIG_ID;
    console.log('[WA-DEBUG] Native flow - appId:', appId, 'configId:', configId);
    if (!appId || !configId) {
      console.error('[WA-DEBUG] Missing appId or configId on native!');
      toast.error('חסר Configuration ID - פנו לתמיכה');
      return;
    }
    const redirectUri = `${import.meta.env.VITE_DASHBOARD_URL}/admin/configurations`;
    const extras = JSON.stringify({ version: 'v4', featureType: 'whatsapp_business_app_onboarding' });
    const params = new URLSearchParams({
      client_id: appId,
      config_id: configId,
      redirect_uri: redirectUri,
      response_type: 'code',
      override_default_response_type: 'true',
      state: 'wa_connect',
      extras
    });
    const oauthUrl = `https://www.facebook.com/dialog/oauth?${params.toString()}`;
    console.log('[WA-DEBUG] Native OAuth URL:', oauthUrl);
    console.log('[WA-DEBUG] Redirect URI:', redirectUri);
    loadingCta.value = true;
    window.open(oauthUrl, '_blank');
    // loadingCta is reset when the App Link callback arrives (see onMounted below)
    return;
  }

  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  console.log('[WA-DEBUG] Web flow - isLocalhost:', isLocalhost, 'hostname:', window.location.hostname);
  if (window.location.protocol !== 'https:' && !isLocalhost) {
    console.error('[WA-DEBUG] Not HTTPS and not localhost, blocking');
    toast.error('חיבור WhatsApp Business דורש חיבור מאובטח (HTTPS). אנא גשו לדף דרך HTTPS.');
    return;
  }

  loadingCta.value = true;
  pendingWabaId = null;
  pendingPhoneNumberId = null;

  try {
    console.log('[WA-DEBUG] Loading FB SDK...');
    await loadFbSdk();
    console.log('[WA-DEBUG] FB SDK loaded successfully');

    const configId = import.meta.env.VITE_META_EMBEDDED_SIGNUP_CONFIG_ID;
    console.log('[WA-DEBUG] Config ID for FB.login:', configId);
    if (!configId) {
      console.error('[WA-DEBUG] Missing VITE_META_EMBEDDED_SIGNUP_CONFIG_ID!');
      toast.error('חסר Configuration ID - פנו לתמיכה');
      loadingCta.value = false;
      return;
    }

    const loginParams = {
      config_id: configId,
      response_type: 'code',
      override_default_response_type: true,
      extras: { version: 'v4', featureType: 'whatsapp_business_app_onboarding' }
    };
    console.log('[WA-DEBUG] Calling FB.login with params:', JSON.stringify(loginParams));

    (window as any).FB.login(
      (response: any) => {
        console.log('[WA-DEBUG] FB.login callback received');
        console.log('[WA-DEBUG] FB.login response:', JSON.stringify(response));
        console.log('[WA-DEBUG] authResponse:', response?.authResponse);
        console.log('[WA-DEBUG] status:', response?.status);

        if (response.authResponse) {
          const code = response.authResponse.code;
          console.log('[WA-DEBUG] Got auth code:', code ? `${code.substring(0, 10)}...` : 'EMPTY');
          console.log('[WA-DEBUG] Pending WABA ID:', pendingWabaId);
          console.log('[WA-DEBUG] Pending Phone Number ID:', pendingPhoneNumberId);
          (async () => {
            try {
              console.log('[WA-DEBUG] Exchanging token with backend...');
              await SettingsApi.exchangeWhatsappToken({
                code,
                wabaId: pendingWabaId ?? undefined,
                phoneNumberId: pendingPhoneNumberId ?? undefined
              });
              console.log('[WA-DEBUG] Token exchange successful!');
              trackEvent('whatsapp_connected_successfully');
              toast.success('WhatsApp Business חובר בהצלחה!');
              await fetchConnectionStatus();
              await adminStore.fetchBusinessData();
            } catch (e) {
              trackEvent('whatsapp_connect_failed');
              console.error('[WA-DEBUG] Token exchange failed:', e);
              toast.error('שגיאה בחיבור WhatsApp Business. אנא נסו שוב.');
            } finally {
              loadingCta.value = false;
            }
          })();
        } else {
          trackEvent('whatsapp_connect_cancelled');
          console.log('[WA-DEBUG] No authResponse - user cancelled or error');
          console.log('[WA-DEBUG] Full response object:', response);
          loadingCta.value = false;
        }
      },
      loginParams
    );
    console.log('[WA-DEBUG] FB.login() called, waiting for popup...');
  } catch (e) {
    console.error('[WA-DEBUG] launchEmbeddedSignup error:', e);
    toast.error('שגיאה בפתיחת חלון ההתחברות');
    loadingCta.value = false;
  }
};

const disconnectWhatsApp = async () => {
  confirm.require({
    message: 'בטוח שאתם רוצים לנתק את WhatsApp Business? ההודעות יישלחו מהמספר הכללי של המערכת',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      try {
        loadingCta.value = true;
        try {
          await SettingsApi.disconnectWhatsapp();
          trackEvent('whatsapp_disconnected');
          toast.success('WhatsApp Business נותק בהצלחה');
          connectionStatus.value = { connected: false };
          await adminStore.fetchBusinessData();
        } catch (e) {
          toast.error('שגיאה בניתוק WhatsApp Business');
        }
        loadingCta.value = false;
      } catch (e) {
        console.error(e)
        toast.error('לא הצלנו למחוק את המשתמש');
      }
    },
  });
};

const handleOAuthCallback = async (code: string) => {
  console.log('[WA-DEBUG] === handleOAuthCallback START ===');
  console.log('[WA-DEBUG] Code received:', code ? `${code.substring(0, 10)}...` : 'EMPTY');
  loadingCta.value = true;
  // On native, the redirect_uri used in the OAuth dialog was VITE_DASHBOARD_URL/admin/configurations
  const redirectUri = `${import.meta.env.VITE_DASHBOARD_URL}/admin/configurations`;
  console.log('[WA-DEBUG] Redirect URI for token exchange:', redirectUri);
  try {
    console.log('[WA-DEBUG] Calling exchangeWhatsappToken...');
    await SettingsApi.exchangeWhatsappToken({ code, redirectUri });
    console.log('[WA-DEBUG] Token exchange successful!');
    toast.success('WhatsApp Business חובר בהצלחה!');
    await fetchConnectionStatus();
    await adminStore.fetchBusinessData();
  } catch (e) {
    console.error('[WA-DEBUG] Token exchange from App Link callback failed:', e);
    toast.error('שגיאה בחיבור WhatsApp Business. אנא נסו שוב.');
  } finally {
    loadingCta.value = false;
  }
};

onMounted(() => {
  window.addEventListener('message', handleFbMessage);
  fetchConnectionStatus();
  // Native flow: Integrations reopens this drawer with the code from Meta's redirect
  if (props.oauthCode) handleOAuthCallback(props.oauthCode);
});
</script>

<style scoped lang="scss">
</style>
